// File: src/services/callService.ts

import apiClient from './apiClient';
import { Call, CallSearch } from '../types/calls';
import { CallDiagnostics, CallRawLogs } from '../types/diagnostics';

interface CallsResponse {
  calls: Call[];
  total: number;
  pages: number;
  current_page: number;
}

export interface CallsFilters {
  page?: number;
  perPage?: number;
  satisfactionFilter?: string;
  /** Data dell'estremo iniziale, formato YYYY-MM-DD. */
  daData?: string;
  /** Ora dell'estremo iniziale, formato HH:mm. Vuota = inizio giornata. */
  daOra?: string;
  /** Data dell'estremo finale, formato YYYY-MM-DD. */
  aData?: string;
  /** Ora dell'estremo finale, formato HH:mm. Vuota = fine giornata. */
  aOra?: string;
  callId?: string;
  /** Numero o sue ultime cifre; il backend confronta solo le cifre, per suffisso. */
  phone?: string;
  /** Testo cercato nel motivo della chiamata. */
  motivo?: string;
  /** '' = tutte, '1' = sì, '0' = no (comprende il dato mancante). */
  trasferita?: '' | '0' | '1';
  vuolePrenotare?: '' | '0' | '1';
}

/**
 * Compone un estremo temporale per il backend.
 *
 * Senza data l'estremo non esiste, anche se l'ora è valorizzata: un orario da
 * solo non individua un istante. Senza ora si manda la data secca, che il
 * backend allarga all'intera giornata — è il caso "voglio un giorno intero".
 *
 * Il valore va passato COSÌ COM'È: è già l'ISO naive che il backend confronta
 * con `start_time`, memorizzato in ora italiana. Nessun `new Date()`, nessun
 * `toISOString()` — quest'ultimo convertirebbe in UTC e sposterebbe il filtro.
 */
const bound = (data?: string, ora?: string): string | undefined =>
  data ? (ora ? `${data}T${ora}` : data) : undefined;

class CallService {
  /**
   * Recupera le chiamate con paginazione e filtri.
   * I filtri non valorizzati non vengono inviati: il backend li tratta come assenti.
   */
  async getCalls(filters: CallsFilters = {}): Promise<CallsResponse> {
    const {
      page = 1, perPage = 10, satisfactionFilter = 'tutti',
      daData, daOra, aData, aOra, callId, phone, motivo, trasferita, vuolePrenotare,
    } = filters;

    try {
      const response = await apiClient.get<CallsResponse>('/api/calls', {
        params: {
          page,
          per_page: perPage,
          satisfaction_filter: satisfactionFilter,
          from: bound(daData, daOra),
          to: bound(aData, aOra),
          call_id: callId?.trim() || undefined,
          phone: phone?.trim() || undefined,
          motivo: motivo?.trim() || undefined,
          trasferita: trasferita || undefined,
          vuole_prenotare: vuolePrenotare || undefined,
        },
      });

      if (!response.data || typeof response.data.calls === 'undefined') {
        console.error("ERRORE: La risposta dell'API non ha il formato atteso. Dati ricevuti:", response.data);
        throw new Error('Formato della risposta API non valido.');
      }
      
      return response.data;
    } catch (error: any) {
      console.error('Errore finale in getCalls:', error);
      throw error;
    }
  }

  /**
   * Recupera i dettagli delle ricerche AI associate a una chiamata.
   * @param callId L'ID della chiamata fornito da Vapi
   * @returns Una promessa che si risolve in un array di oggetti CallSearch
   */
  async getCallSearches(callId: string): Promise<CallSearch[]> {
    try {
      // Usa un percorso RELATIVO - apiClient aggiungerà automaticamente il baseURL
      const response = await apiClient.get<{ searches: CallSearch[] }>(
        `/api/vapi/call-searches/${callId}`
      );

      // Controllo di sicurezza sul formato della risposta
      if (!response.data || !Array.isArray(response.data.searches)) {
        console.error("ERRORE: La risposta dall'endpoint delle ricerche non ha il formato atteso. Dati ricevuti:", response.data);
        throw new Error('Formato della risposta API per le ricerche non valido.');
      }

      return response.data.searches;

    } catch (error: any) {
      // Gestione degli errori in stile Axios, coerente con getCalls
      if (error.response) {
        // Errore restituito dal server (es. 403, 404, 500)
        console.error('Errore dal backend in getCallSearches (dati):', error.response.data);
        const apiError = error.response.data.details || error.response.data.error || 'Errore durante il recupero delle ricerche IA.';
        throw new Error(apiError);
      } else if (error.request) {
        // La richiesta è partita ma non ha ricevuto risposta
        console.error('Errore di rete in getCallSearches (richiesta):', error.request);
        throw new Error('Nessuna risposta dal server. Controlla che il backend Flask sia in esecuzione.');
      } else {
        // Errore generico
        console.error('Errore generico in getCallSearches:', error.message);
        throw error;
      }
    }
  }

  /**
   * URL firmato e a breve scadenza della registrazione. Il backend registra
   * l'accesso nell'audit: va chiamato solo quando l'operatore sceglie di ascoltare.
   */
  async getRecordingUrl(callId: string): Promise<string> {
    const response = await apiClient.get<{ url: string }>(`/api/vapi/call/${callId}/recording`);
    return response.data.url;
  }

  /**
   * Invia una segnalazione per una chiamata.
   * L'identità del segnalante (nome + email) è derivata lato backend dal token JWT dell'operatore autenticato.
   * @param callId ID della chiamata
   * @param reason Motivazione della segnalazione
   * @param category Categoria dell'errore (es. INFORMAZIONI, PRENOTAZIONI, ecc.)
   */
  async reportCall(callId: string, reason: string, category: string): Promise<void> {
    try {
      // Usa apiClient per mantenere la coerenza con il resto del codice (BaseURL, Auth, ecc.)
      await apiClient.post(`/api/calls/${callId}/report`, {
        reason,
        category,
      });
      // Se la promise si risolve senza errori, significa che è andato tutto a buon fine (200 OK)
    } catch (error: any) {
      console.error('Errore in reportCall:', error);
      if (error.response) {
        const msg = error.response.data.error || error.response.data.message || 'Errore durante l\'invio della segnalazione.';
        throw new Error(msg);
      } else if (error.request) {
        throw new Error('Nessuna risposta dal server durante la segnalazione.');
      }
      throw error;
    }
  }

  /**
   * Log tecnico della chiamata, ridotto dal backend a modello diagnostico.
   * Con la ZDR attiva è l'unico modo di sapere perché una chiamata è andata
   * storta: su Vapi non resta più nulla da consultare.
   */
  async getCallDiagnostics(callId: string): Promise<CallDiagnostics> {
    try {
      const response = await apiClient.get<CallDiagnostics>(
        `/api/vapi/call/${callId}/diagnostics`
      );
      return response.data;
    } catch (error: any) {
      console.error('Errore in getCallDiagnostics:', error);
      if (error.response) {
        const msg = error.response.data?.error || 'Errore nel recupero del log tecnico.';
        throw new Error(msg);
      } else if (error.request) {
        throw new Error('Nessuna risposta dal server durante il recupero del log.');
      }
      throw error;
    }
  }

  /**
   * Righe grezze del log tecnico. Separate dalla diagnostica perché pesano
   * due ordini di grandezza di più: si scaricano solo se qualcuno le apre.
   */
  async getCallRawLogs(callId: string): Promise<CallRawLogs> {
    try {
      const response = await apiClient.get<CallRawLogs>(`/api/vapi/call/${callId}/logs`);
      return response.data;
    } catch (error: any) {
      console.error('Errore in getCallRawLogs:', error);
      if (error.response) {
        throw new Error(error.response.data?.error || 'Errore nel recupero del log grezzo.');
      } else if (error.request) {
        throw new Error('Nessuna risposta dal server durante il recupero del log.');
      }
      throw error;
    }
  }
}

export const callService = new CallService();
